/**
 * EmploymentRoleFormModal — Create/edit employment role with FormModal pattern.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Hash, Type, FileText, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import {
  useCreateEmploymentRole,
  useUpdateEmploymentRole,
} from '../hooks';
import type { EmploymentRole, EmploymentRoleFormData } from '../types';

interface EmploymentRoleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: EmploymentRole | null;
}

export function EmploymentRoleFormModal({
  isOpen,
  onClose,
  editData,
}: EmploymentRoleFormModalProps) {
  const { t } = useTranslation();
  const createMutation = useCreateEmploymentRole();
  const updateMutation = useUpdateEmploymentRole();
  const isEdit = !!editData;

  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: isEdit ? 'EDIT' : 'NEW',
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [description, setDescription] = useState('');
  const [hasBaseSalary, setHasBaseSalary] = useState(false);
  const [baseRate, setBaseRate] = useState('0');
  const [baseRatePeriod, setBaseRatePeriod] = useState('monthly');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setCode(editData.code);
        setName(editData.name);
        setNameEn(editData.nameEn || '');
        setDescription(editData.description || '');
        setHasBaseSalary(editData.hasBaseSalary ?? false);
        setBaseRate(String(editData.baseRate ?? 0));
        setBaseRatePeriod(editData.baseRatePeriod || 'monthly');
      } else {
        setCode('');
        setName('');
        setNameEn('');
        setDescription('');
        setHasBaseSalary(false);
        setBaseRate('0');
        setBaseRatePeriod('monthly');
      }
      setError('');
    }
  }, [isOpen, editData]);

  const isFormValid = useMemo(() => {
    return !!(code.trim() && name.trim());
  }, [code, name]);

  const validateForm = (): boolean => {
    setError('');
    if (!code.trim()) { setError(t('employmentRoles.codeRequired')); return false; }
    if (!name.trim()) { setError(t('employmentRoles.nameRequired')); return false; }
    return true;
  };

  const handleSubmit = () => {
    const payload: EmploymentRoleFormData = {
      code: code.trim(),
      name: name.trim(),
      nameEn: nameEn.trim() || undefined,
      description: description.trim() || undefined,
      hasBaseSalary,
      baseRate: hasBaseSalary ? (parseFloat(baseRate) || 0) : 0,
      baseRatePeriod: hasBaseSalary ? baseRatePeriod : 'monthly',
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () =>
        isEdit
          ? updateMutation.mutateAsync({ id: editData!.id, data: payload })
          : createMutation.mutateAsync(payload),
      onSuccess: () => {
        toast.success(isEdit ? t('employmentRoles.updateSuccess') : t('employmentRoles.createSuccess'));
        onClose();
      },
      onError: (err: any) => {
        const msg = err.response?.data?.message || '';
        setError(msg || (isEdit ? t('employmentRoles.updateError') : t('employmentRoles.createError')));
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('employmentRoles.edit') : t('employmentRoles.create')}
      isPending={isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel={isEdit ? 'EDIT' : 'NEW'}
      error={error}
    >
      <div className="space-y-3">
        <FormField icon={<Hash className="h-4 w-4" />} label={t('employmentRoles.code')} required>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isEdit}
            placeholder={t('employmentRoles.codePlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<Type className="h-4 w-4" />} label={t('employmentRoles.name')} required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('employmentRoles.namePlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<Type className="h-4 w-4" />} label={t('employmentRoles.nameEn')}>
          <input
            type="text"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder={t('employmentRoles.nameEnPlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<FileText className="h-4 w-4" />} label={t('employmentRoles.description')}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('employmentRoles.descriptionPlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' as const }}
          />
        </FormField>

        <FormField icon={<DollarSign className="h-4 w-4" />} label={t('employmentRoles.hasBaseSalary')}>
          <button
            type="button"
            onClick={() => setHasBaseSalary(!hasBaseSalary)}
            className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer"
            style={{ backgroundColor: hasBaseSalary ? 'var(--color-primary-500)' : MODAL_COLORS.border }}
          >
            <span
              className="inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform"
              style={{ transform: hasBaseSalary ? 'translateX(24px)' : 'translateX(4px)' }}
            />
          </button>
        </FormField>

        {hasBaseSalary && (
          <>
            <FormField icon={<DollarSign className="h-4 w-4" />} label={t('employmentRoles.baseRate')} required>
              <input
                type="number"
                value={baseRate}
                onChange={(e) => setBaseRate(e.target.value)}
                min="0"
                step="0.01"
                className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
                style={inputStyle}
              />
            </FormField>

            <FormField icon={<DollarSign className="h-4 w-4" />} label={t('employmentRoles.baseRatePeriod')}>
              <select
                value={baseRatePeriod}
                onChange={(e) => setBaseRatePeriod(e.target.value)}
                className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
                style={inputStyle}
              >
                <option value="monthly">{t('employmentRoles.monthly')}</option>
                <option value="hourly">{t('employmentRoles.hourly')}</option>
                <option value="daily">{t('employmentRoles.daily')}</option>
              </select>
            </FormField>
          </>
        )}
      </div>
    </FormModal>
  );
}
